import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ContentMapperService } from '../../../core/services/content-mapper-service';
import { ToastrService } from 'ngx-toastr';
import user_endpoint from '../../../data/api-endpoint/user-endpoint';
import { CommonService } from '../../../core/services/common.service';
import { Subscription} from 'rxjs';
import { Loader } from '../../../../shared/components/loader/loader';

@Component({
  selector: 'app-create-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,Loader],
  templateUrl: './create-user.html',
  styleUrl: './create-user.scss'
})
export class CreateUser implements OnInit, OnDestroy {


  profileImageUrl: string | null = null;
  defaultAvatar: string = 'assets/img/default-avatar.svg';
  editMode: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();


  createUserForm: FormGroup<any> = new FormGroup<any>({});
  loading = false;
  roles: string[] = ['user', 'admin'];
  permissions: any[] = []
  adminPermissions: number[] = []
  disablePermissions:boolean = false;
  user_id: number | null = null;
  isOpen:boolean = false

  private subscription: Subscription = new Subscription();



  constructor(
    private fb: FormBuilder,
    private contentMapper: ContentMapperService,
    private toastr: ToastrService,
    private commonService: CommonService
  ) {
    
  }

  ngOnInit(): void {
    this.fetchPermissionList();
    this.subscription = this.commonService.getUserModal().subscribe((config:any) => {
      if(config.isOpen && config.type === 'Edit User' && config.userId){
        this.editMode = true;
        this.user_id = config.userId;
        this.getCurrentUser(config.userId);
      }else{
        this.disablePermissions = false
        this.editMode = false;
      }
      this.isOpen = config.isOpen;
      this.initializeForm();
      this.profileImageUrl = null;
    });

  }

  private initializeForm(): void {
    this.createUserForm = this.fb.group({
      name:['',this.editMode ? Validators.required : null],
      email: ['', [Validators.required, Validators.email]],
      role: ['user', Validators.required],
      permissions: [[]]
    });
  }


  fetchPermissionList(): void {
    this.contentMapper.get(user_endpoint.permissionList).subscribe({
      next: (response: any) => {
        this.permissions = response.results;
        this.adminPermissions = this.permissions.map((permission: any) => permission.id);

      }, error: (error: any) => {
        this.permissions = [];
        this.adminPermissions = [];
        console.error('Error occured while fetching permission list', error.message);
      }
    })
  }

  onPermissionChange(permission: string, event: any): void {
    const currentPermissions = this.createUserForm.get('permissions')?.value || [];
    
    if (event.target.checked) {
      if (!currentPermissions.includes(permission)) {
        currentPermissions.push(permission);
      }
    } else {
      const index = currentPermissions.indexOf(permission);
      if (index > -1) {
        currentPermissions.splice(index, 1);
      }
    }
    
    this.createUserForm.patchValue({ permissions: currentPermissions });
  }

  isPermissionSelected(permission: string): boolean {
    const currentPermissions = this.createUserForm.get('permissions')?.value || [];
    return currentPermissions.includes(permission);
  }

  onSubmit(): void {
    if (this.createUserForm.valid) {
      this.loading = true;
      
      const formData = this.createUserForm.getRawValue(); 

      const final_data = {
        email: formData.email,
        role: formData.role,
        permissions: this.userRole ? this.adminPermissions : formData.permissions
      }

      this.contentMapper.post('user', final_data).subscribe({
        next: (_: any) => {
          this.loading = false;
          this.toastr.success('User created successfully!', 'Success');
          this.reload.emit();
          this.disablePermissions = false
        },
        error: (error: any) => {
          this.toastr.error(error.error?.message || 'Failed to create user', 'Error');
          this.loading = false;
        }
      });
    } else {
      this.toastr.error('Please fill all required fields correctly', 'Validation Error');
    }
  }

  onRoleChange(event: any): void {
    const role = event.target.value;
    if (role === 'admin') {
      this.createUserForm.get('permissions')?.setValue(this.adminPermissions);
      this.disablePermissions = true;
    } else {
      this.createUserForm.get('permissions')?.setValue([]);
      this.disablePermissions = false
    }
  }

  get userRole(): boolean {
    return  this.createUserForm.get('role')?.value === 'admin';
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  onCancel(): void {
    this.close.emit();
  }

  onImageError(): void {
    // Set profileImageUrl to null to trigger the default avatar display
    this.profileImageUrl = null;
    console.warn('Profile image failed to load, showing default avatar');
  }

  getCurrentUser(user_id: any): void {
    this.loading = true;
    this.contentMapper.get(`${user_endpoint.user}/${user_id}`).subscribe({
      next: (response: any) => {
        const user_detail = response.results[0];
        
        if (user_detail) {
          this.createUserForm.patchValue({
            name: user_detail.name,
            email: user_detail.email,
            role: user_detail.role,
            permissions: user_detail.permissions_ids || []
          });

          this.profileImageUrl = user_detail.picture && user_detail.picture.trim() !== '' 
            ? user_detail.picture 
            : null;
          
          if (user_detail.role === 'admin') {
            this.disablePermissions = true;
          } else {
            this.disablePermissions = false;
          }
        }
        this.loading = false;

      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error occurred while fetching current user', error.message);
        this.toastr.error('Failed to load user details', 'Error');
      }
    });
  }

  updateUser(): void {
    this.loading = true;

    const formData = this.createUserForm.getRawValue();
    const final_data = {
      email: formData.email,
      role: formData.role,
      permissions: this.userRole ? this.adminPermissions : formData.permissions
    }
    this.contentMapper.put(`${user_endpoint.user}/${this.user_id}`, final_data).subscribe({
      next: (_: any) => {
        this.loading = false;
        this.toastr.success('User updated successfully!', 'Success');
        this.reload.emit();

      }, error: (error: any) => {
        this.toastr.error(error.error?.message || 'Failed to update user', 'Error');
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    if(this.subscription){
      this.subscription.unsubscribe();
    }
  }
}
